/**
 * What each nav destination is actually called once you land on it.
 *
 * Menu labels and on-page headings drift apart: the menu says "Studio
 * Settings", the page says "Configure your studio"; the menu says "LP
 * Enrollments", the page says "Learning path enrollments". People search for
 * whichever one they last read, so search has to know both.
 *
 * `heading` and `description` are transcribed from the page's own H1 and
 * subheading — they are not invented copy, and they should be re-transcribed
 * (not paraphrased) whenever a page's header text changes. `aliases` cover
 * renames and the shorthand a page is known by, so old vocabulary keeps working
 * after a rename.
 *
 * Two tests keep this honest: `navigationPageIndex.test.js` asserts every key
 * still resolves to a live route, and `navigationPageHeadings.test.js` asserts
 * every heading and description here is still rendered by that route. So record
 * only what those can check — a heading that just repeats the menu label, or one
 * assembled at runtime, should be an alias instead.
 *
 * See docs/navigation-search.md for what those guards cannot catch (chiefly
 * missing aliases, which only real search traffic reveals) and the options for
 * closing that gap.
 */

import { isOpaqueIdSegment } from "@/lib/navigation/page-search-match";

/**
 * Collapse a live href into a stable directory key: drop the locale prefix,
 * query string, and trailing slash, and replace the workspace id with a
 * placeholder so per-studio URLs share one entry.
 *
 * The workspace segment is recognised by the shape of an id, not by listing the
 * sibling routes it isn't. A denylist of static segments (`admin`, `reports`, …)
 * would go quietly wrong the day someone adds `/workspaces/settings`: that URL
 * would normalise to a studio-id key and lose its directory entry with no
 * failing test to show for it.
 */
export function normalizeRouteKey(href) {
  if (typeof href !== "string" || !href.trim()) return "";

  const [pathWithHash] = href.split("?", 1);
  const [path, hash = ""] = pathWithHash.split("#", 2);

  const normalizedPath = path
    .replace(/^\/(es|fr-CA)(?=\/|$)/, "")
    .replace(/^\/workspaces\/([^/]+)/, (match, segment) =>
      isOpaqueIdSegment(segment) ? "/workspaces/:workspaceId" : match,
    )
    .replace(/\/+$/, "")
    .toLowerCase();

  return hash ? `${normalizedPath}#${hash.toLowerCase()}` : normalizedPath;
}

const PAGE_DIRECTORY = {
  // ---------------------------------------------------------------- Learn
  "/playbook": {
    aliases: ["company overview", "positioning", "glossary", "lessons"],
  },
  "/playbook#our-content-faq": { aliases: ["faq", "faqs", "questions"] },
  "/playbook#fbiv": { aliases: ["fbiv", "features benefits impact value"] },
  "/playbook#messaging": { aliases: ["pitch", "pitches", "value message"] },
  "/playbook#cold-call": { aliases: ["cold call", "outbound", "inbound"] },
  "/dashboard/learning-paths": {
    heading: "Learning Paths",
    aliases: ["lp", "courses", "curriculum"],
  },
  "/training": {
    heading: "Your Training",
    aliases: ["my training", "to do", "assigned work"],
  },
  "/stories": { heading: "Stories" },

  // ------------------------------------------------------------- Practice
  "/simulator": {
    aliases: ["simulator", "roleplay", "scenarios", "practice scenarios"],
  },
  "/simulator/personas": {
    heading: "Personas Playbook",
    aliases: ["buyer personas", "customer personas"],
  },
  "/simulator/seller-personas": {
    heading: "Other Personas",
    aliases: ["seller personas", "rep personas"],
  },
  "/simulator/on-the-fly": {
    heading: "On-the-Fly Practice",
    aliases: ["ad hoc practice", "quick practice"],
  },

  // ------------------------------------------------------------ Execution
  "/dem-cog/reports": {
    heading: "Deal Diagnostic Readouts",
    aliases: ["deal clinic", "cog", "cog report", "deal review"],
  },
  "/dem-cog/dems": {
    heading: "Discovery Expert Models",
    aliases: ["dem", "dems", "expert models"],
  },
  "/dashboard/transcript-results": {
    aliases: [
      "conversations",
      "conversation analyzer",
      "transcript analysis",
      "call analysis",
      "analyze a call",
      "recorded calls",
    ],
  },
  "/guide": {
    heading: "Live guidance for real conversations",
    description:
      "Guide listens and hands you the next line, the right move, and what to watch for, in the moment. On a computer it floats over Zoom, Meet or Teams.",
    aliases: [
      "live coach",
      "live call coaching",
      "live feedback",
      "call coach",
      "real-time coaching",
    ],
  },
  "/workspaces/scripts": {
    heading: "Script Generator",
    aliases: ["email scripts", "talking scripts"],
  },

  // ------------------------------------------------------------- Results
  "/dashboard": {
    heading: "My Results",
    description: "Track your results and assignments",
    aliases: ["my scores", "my progress", "home dashboard"],
  },
  "/leaderboard": { aliases: ["rankings", "standings"] },
  "/dashboard/assignments": {
    heading: "Assignments",
    description:
      "Practice scenarios assigned to you. Tests and learning paths are tracked separately under Tests and Learning Paths.",
    aliases: ["my assignments", "assigned practice", "pending work"],
  },
  "/dashboard/tests": {
    heading: "Tests",
    description: "Complete your assigned knowledge assessments.",
    aliases: ["quizzes", "knowledge checks", "assessments"],
  },
  "/dashboard/okrs": {
    heading: "OKRs",
    aliases: ["objectives", "key results", "goals"],
  },
  "/dashboard/badges": { heading: "Badges", aliases: ["achievements"] },
  "/dashboard/feedback-analysis": {
    aliases: ["my coaching", "ai coaching"],
  },
  "/dashboard/feedback-comments": {
    heading: "Feedback",
    aliases: ["comments", "my feedback"],
  },
  "/dashboard/persona-chats": {
    heading: "Persona chats",
    aliases: ["chat history", "persona conversations"],
  },
  "/chats": { aliases: ["conversations", "past chats"] },

  // ------------------------------------------------------------- Reports
  "/workspaces/reports": {
    heading: "All results",
    aliases: ["all reports", "team results", "reporting"],
  },
  "/workspaces/reports/manager-dashboard": {
    heading: "Manager Dashboard",
    aliases: ["team overview", "manager view"],
  },
  "/workspaces/reports/execution": {
    heading: "Execution module usage",
    aliases: ["module usage", "adoption"],
  },
  "/workspaces/reports/transcript-results": {
    aliases: ["transcripts", "call analysis"],
  },
  // These four share one [period] route whose heading is assembled from the
  // period label at runtime, so there is no fixed heading to record.
  "/workspaces/reports/engagement/quarterly": {
    aliases: ["by quarter", "quarterly report", "quarterly engagement"],
  },
  "/workspaces/reports/engagement/monthly": {
    aliases: ["by month", "monthly report", "monthly engagement"],
  },
  "/workspaces/reports/engagement/weekly": {
    aliases: ["by week", "weekly report", "weekly engagement"],
  },
  "/workspaces/reports/engagement/custom": {
    aliases: ["custom range", "custom dates", "by date range"],
  },
  "/workspaces/reports/enrollments": {
    heading: "Learning path enrollments",
    aliases: [
      "lp enrollments",
      "enrolled learners",
      "time tracking",
      "time spent",
      "hours",
    ],
  },
  "/workspaces/reports/overdue-items": {
    heading: "Overdue items",
    aliases: ["late work", "past due"],
  },
  "/workspaces/reports/tests/multiple-choice": {
    heading: "Multiple choice tests",
    description: "Review how your team performed on multiple choice tests.",
  },
  "/workspaces/reports/tests/faq": {
    heading: "Q&A tests",
    description: "Review how your team performed on Q&A tests.",
    aliases: ["faq tests"],
  },
  "/workspaces/reports/tests/glossary": {
    heading: "Glossary tests",
    description: "Review how your team performed on glossary tests.",
  },
  "/workspaces/reports/recordings": {
    heading: "Recordings library",
    aliases: ["call recordings", "audio"],
  },
  "/workspaces/reports/coaching-report": {
    heading: "Coaching report",
    aliases: ["per rep coaching"],
  },
  "/workspaces/reports/team-coaching": {
    heading: "Team Coaching Report",
    description: "Aggregate performance insights across your team",
  },
  "/workspaces/reports/feedback-comments": {
    heading: "Studio feedback",
    aliases: ["feedback comments"],
  },
  "/workspaces/reports/practice-sessions-export": {
    heading: "Practice sessions export",
    aliases: ["export sessions", "csv export", "download sessions"],
  },

  // --------------------------------------------------------------- Admin
  "/workspaces/:workspaceid/edit": {
    heading: "Configure your studio",
    description:
      "Customize your studio's profile, language, notifications, and connections.",
    aliases: [
      "studio settings",
      "workspace settings",
      "configure studio",
      "studio profile",
      "notifications",
      "sales language",
      "connections",
      "integrations",
      "training modules",
      "plugins",
    ],
  },
  "/workspaces/:workspaceid/positioning": {
    heading: "Positioning",
    aliases: ["value proposition", "messaging foundation"],
  },
  "/workspaces/admin/data/content": {
    heading: "Content",
    aliases: ["resources", "documents", "decks", "uploads"],
  },
  // The per-group pages under /glossary/groups/[groupId] cannot have their own
  // entries: a directory key has to resolve to a live nav destination (see
  // navigationPageIndex.test.js) and a group page's URL is only known once a
  // group exists. Their vocabulary rides along here instead, on the library
  // that lists them.
  "/workspaces/admin/glossary": {
    heading: "Glossary",
    aliases: ["terms", "definitions", "glossary groups", "term groups"],
  },
  "/workspaces/admin/faqs": {
    heading: "Q&As",
    aliases: ["faq", "faqs", "questions and answers"],
  },
  "/workspaces/admin/fbiv": {
    heading: "Capability Maps",
    aliases: ["fbiv", "features benefits impact value"],
  },
  "/workspaces/admin/takeaway-reports": {
    heading: "Takeaway reports",
    description:
      "Capture key insights from simulations and conversations as reusable reports for your team.",
    aliases: ["key insights", "takeaways"],
  },
  "/workspaces/admin/people": {
    heading: "Studio members",
    aliases: ["users", "team members", "seats", "invite"],
  },
  "/workspaces/admin/people/communities": {
    heading: "Communities",
    description: "Create and manage communities to organize your members.",
    aliases: ["groups", "teams"],
  },
  "/workspaces/admin/role-plays": {
    heading: "Assignments",
    description: "Create and manage assignments for your team.",
    aliases: ["roleplays", "assign practice"],
  },
  "/workspaces/admin/learning-paths": {
    heading: "Learning Path Management",
    description: "Build and assign multi-step learning paths for your team.",
    aliases: ["lp builder", "build a course"],
  },
  "/workspaces/admin/tests": {
    heading: "Test management",
    description: "Create multiple-choice tests and assign them to your team.",
    aliases: ["quizzes", "knowledge checks"],
  },
  "/workspaces/admin/faq-tests": {
    heading: "Q&A test management",
    description: "Create Q&A-based tests and assign them to your team.",
    aliases: ["faq tests", "faq quizzes"],
  },
  "/workspaces/admin/glossary-tests": {
    heading: "Glossary test management",
    description: "Create glossary-based tests and assign them to your team.",
    aliases: ["term tests", "vocabulary quizzes"],
  },
  "/workspaces/admin/fbiv-tests": {
    heading: "Capability Map test management",
    description: "Create Capability Map tests and assign them to your team.",
    aliases: ["fbiv tests"],
  },
  "/workspaces/admin/okrs": {
    heading: "Objectives and key results",
    description: "Set objectives and track key results across your team.",
    aliases: ["goals"],
  },
  "/workspaces/admin/badges": {
    heading: "Badges",
    description: "Recognize milestones and achievements across your team.",
  },
  "/workspaces/admin/analytics": {
    heading: "Analytics",
    aliases: ["usage", "activity breakdown"],
  },
  "/workspaces/admin/data/modules": {
    heading: "Modules manager",
    description:
      "Manage your training modules and customize their descriptions.",
    aliases: ["training modules", "enable module", "module configuration"],
  },
  "/workspaces/admin/data/personas": {
    heading: "Buyer personas",
    description: "Manage and organize your buyer personas.",
  },
  // The heading is rendered by StoryPromptSettingsPage, not the route's own
  // files, so the heading test cannot verify it. Keep the wording as aliases.
  "/workspaces/admin/data/stories": {
    aliases: ["story prompts", "story builder", "story settings"],
  },
  "/workspaces/admin/data/rubrics": {
    heading: "Studio rubrics",
    aliases: ["scorecards", "grading", "scoring"],
  },
  "/workspaces/admin/data/discovery": {
    // These scenario headers use the studio's module name at runtime. Keep
    // their former names searchable without claiming a fixed on-page heading.
    aliases: ["Discovery", "discovery tracks"],
  },
  "/workspaces/admin/data/pitch": {
    aliases: ["Messaging / pitches", "pitches", "messaging"],
  },
  "/workspaces/admin/data/cold-call": {
    heading: "Outbound & Inbounds",
    description: "Manage your Outbound & Inbound scenarios.",
  },
  "/workspaces/admin/data/objections": {
    aliases: ["Objection handling", "customer objections"],
  },
  "/workspaces/admin/data/plays": {
    aliases: ["Play scenarios", "sales plays"],
  },
  "/workspaces/admin/phone-practice": {
    heading: "Phone practice",
    description:
      "Let users run scored practice sessions over a real phone call. Calls cost per minute, so the feature is off by default.",
    aliases: ["phone role play", "call practice", "vapi"],
  },
  "/workspaces/admin/data/management-scenarios": {
    heading: "Management scenarios",
    description: "Manage your management practice scenarios.",
  },
  "/workspaces/admin/data/seller-personas": {
    // The heading ("Other personas") is rendered by the shared
    // SellerPersonasList component, not the route's own files, so the
    // heading test cannot verify it. Keep the wording searchable as aliases.
    aliases: ["other personas", "seller personas", "rep personas"],
  },
  "/workspaces/broadcast": {
    heading: "Broadcast Messages",
    aliases: ["announcement", "message everyone", "email the team"],
  },
  "/help/admin": {
    heading: "Admin Help Center",
    aliases: ["documentation", "how to"],
  },

  // ------------------------------------------------------------- Off-menu
  "/settings": {
    heading: "Account Settings",
    aliases: [
      "my profile",
      "password",
      "timezone",
      "email notifications",
      "two factor",
    ],
  },
  "/help": {
    heading: "How can we help?",
    aliases: ["support", "documentation", "contact us"],
  },
  "/help/member": {
    heading: "Member Help Center",
    aliases: ["how to", "user guide", "learner help"],
  },
  // Its H1 comes from content.json, which the heading guard cannot read.
  "/help/member/practice-sessions": {
    aliases: [
      "how does a practice session work",
      "practice mode",
      "live mic mode",
      "scored mode",
      "role-play instructions",
    ],
  },
  // Its H1 comes from content.json, which the heading guard cannot read.
  "/help/admin/ai-assistants": {
    aliases: [
      "salesably in claude",
      "salesably in chatgpt",
      "codex",
      "ai assistant",
      "connector",
      "mcp",
    ],
  },
};

/**
 * Destinations people search for that no top-nav menu lists — reachable from
 * the avatar menu or a footer link, and previously unfindable through search.
 *
 * `requiresWorkspace` mirrors the gate on the control that links to them, the
 * same way every other entry in `navigation-pages.js` carries `display:
 * hasWorkspace`. Note that it does not currently decide anything:
 * `searchNavigationPages` needs a studio to resolve labels and module gating at
 * all, so it returns nothing before the flag is read. It records what the page
 * needs, so the answer is already right if that entry condition ever widens.
 */
export const OFF_MENU_SEARCH_PAGES = [
  {
    href: "/settings",
    label: "Account Settings",
    section: "Account",
    requiresWorkspace: true,
  },
  {
    href: "/help",
    label: "Help Center",
    section: "Support",
    requiresWorkspace: false,
  },
  {
    href: "/help/member",
    label: "Member Help Center",
    section: "Support",
    requiresWorkspace: false,
  },
  {
    href: "/help/member/practice-sessions",
    label: "Practice Sessions Guide",
    section: "Support",
    requiresWorkspace: false,
  },
  {
    href: "/help/admin/ai-assistants",
    label: "Salesably in Claude and ChatGPT",
    section: "Support",
    requiresWorkspace: false,
  },
];

export function getPageDirectoryEntry(href) {
  return PAGE_DIRECTORY[normalizeRouteKey(href)] || null;
}

/** Every route this directory describes — used to test for stale entries. */
export function getPageDirectoryKeys() {
  return Object.keys(PAGE_DIRECTORY);
}

/** Route key → entry pairs, for tests that verify entries against page source. */
export function getPageDirectoryEntries() {
  return Object.entries(PAGE_DIRECTORY);
}
